import {CurrencyPipe, DecimalPipe} from '@angular/common'
import {HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi} from '@angular/common/http'
import {
  type ApplicationConfig,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core'
import {getApp, initializeApp, provideFirebaseApp} from '@angular/fire/app'
import {initializeFirestore, provideFirestore} from '@angular/fire/firestore'
import {MAT_DATE_LOCALE, provideNativeDateAdapter} from '@angular/material/core'
import {MAT_SELECT_CONFIG} from '@angular/material/select'
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async'
import {PreloadAllModules, RouteReuseStrategy, provideRouter, withPreloading} from '@angular/router'
import {JWT_OPTIONS, JwtModule} from '@auth0/angular-jwt'
import {provideToastNotification} from '@common/components/toast-notification/toast-notification.provider'
import {provideAiCore} from '@core/ai'
import {provideInitialSyncCore} from '@core/sync/initial'
import {CoreEffects} from '@core/store/core.effects'
import {coreReducer} from '@core/store/core.reducer'
import {environment} from '@environments/environment'
import {ChatFacade} from '@features/echo/services/chat.facade'
import {IonicRouteStrategy, mdTransitionAnimation, provideIonicAngular} from '@ionic/angular/standalone'
import {provideEffects} from '@ngrx/effects'
import {combineReducers, provideState, provideStore} from '@ngrx/store'
import {provideStoreDevtools} from '@ngrx/store-devtools'
import {TranslateLoader, TranslateModule} from '@ngx-translate/core'
import {HomeEffects} from '@pages/home/store/home.effects'
import {homeReducer} from '@pages/home/store/home.reducer'
import {ActivitiesEffects} from '@pages/outlet-detail/store/actitivies/activities.effects'
import {activityReducer} from '@pages/outlet-detail/store/actitivies/activities.reducer'
import {OrdersEffects} from '@pages/outlet-detail/store/orders/orders.effects'
import {ordersReducer} from '@pages/outlet-detail/store/orders/orders.reducers'
import {OutletDetailsEffects} from '@pages/outlet-detail/store/outlet-details.effects'
import {outletDetailsReducer} from '@pages/outlet-detail/store/outlet-details.reducer'
import {ScheduleEffects} from '@pages/schedule/store/schedule.effects'
import {scheduleReducer} from '@pages/schedule/store/schedule.reducer'
import {DayLabelPipe} from '@pipes/date-label.pipe'
import {FormatDatePipe} from '@pipes/format-date.pipe'
import {NoImagePipe} from '@pipes/images/no-image.pipe'
import {SecureNoImagePipe} from '@pipes/images/secure-no-image.pipe'
import {IsSyncPipe} from '@pipes/is-sync.pipe'
import {ProductConservationConditionTextPipe} from '@pipes/product/product-conservation-condition-text.pipe'
import {ProductImagePipe} from '@pipes/product/product-image.pipe'
import {SafePipe} from '@pipes/safe.pipe'
import {TranslateStringV2Pipe} from '@pipes/translate-string-v2.pipe'
import {UveTranslateStringPipe} from '@pipes/translate-string.pipe'
import {TruncatePipe} from '@pipes/truncate.pipe'
import {UveNumberPipe} from '@pipes/uve-number.pipe'
import {UveOrderNumberPipe} from '@pipes/uve-order-number.pipe'
import {UveTranslatePipe} from '@pipes/uve-translate.pipe'
import {InitService} from '@service/init/init.service'
import {provideMapCore} from '@service/map/map.providers'
import {StreetTranslocoService} from '@service/street-transloco.service'
import {SessionService as AppSessionService, provideApiClient} from '@uve-solutions/api-angular-client'
import {SessionService, provideSlaveSessionService} from '@uve-solutions/composition/session'
import {ChatContract} from '@uve-solutions/f-chat/core'
import {SHARED_CORE_EVENT_BUS, eventBusProviders} from '@uve-solutions/services'
import {ENVIRONMENT, provideSharedDomainPipes} from '@uve-solutions/shared-domain'
import {provideTranslationsOnlyUveI18n} from '@uve-solutions/translations'
import {SafeHtmlPipe} from '@uve-solutions/ui/pipes'
import APP_ROUTES from './app.routes'
import {WebpackTranslateLoader, appInitializerFactory, sandboxActive, uveApiClientEnv} from './app.utils'
import {BlobXhrInterceptor} from './interceptors/blob-xhr.interceptor'
import {HttpCancelInterceptor} from './interceptors/http-cancel.interceptor'
import {ErrorInterceptor} from './interceptors/http.interceptor'
import {UnauthorizedLoggingInterceptor} from './interceptors/unauthorized-logging.interceptor'
import {HttpMonitoringSink} from '@service/monitoring/http-monitoring.sink'
import {MONITORING_SINK, MONITORING_SINK_URL_KEY} from '@service/monitoring/monitoring-sink'
import {QueueMonitoringSink} from '@service/monitoring/queue-monitoring.sink'
import {SERVICE_PROVIDERS} from './providers/providers'
import {provideAiChat, provideAiChatAuth} from '@uve-solutions/f-ai-chat'
import {createSessionAuthAdapter} from '@uve-solutions/f-ai-chat/core'
import {provideAiChatChartComponents} from '@uve-solutions/f-ai-chat/charts'
import {provideAiChatAdvancedChartComponents} from '@uve-solutions/f-ai-chat/echarts-advanced'
import {provideAiChatBuiltinComponents} from '@uve-solutions/f-ai-chat/components'

const providers: ApplicationConfig['providers'] = [
  provideBrowserGlobalErrorListeners(),
  provideZoneChangeDetection({eventCoalescing: true}),

  {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
  provideIonicAngular({
    mode: 'ios',
    swipeBackEnabled: false,
    navAnimation: mdTransitionAnimation,
    useSetInputAPI: true,
  }),

  provideHttpClient(withFetch(), withInterceptorsFromDi()),
  {provide: HTTP_INTERCEPTORS, useClass: BlobXhrInterceptor, multi: true},
  {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
  {provide: HTTP_INTERCEPTORS, useClass: HttpCancelInterceptor, multi: true},
  {provide: HTTP_INTERCEPTORS, useClass: UnauthorizedLoggingInterceptor, multi: true},
  provideRouter(APP_ROUTES, withPreloading(PreloadAllModules)),

  provideAnimationsAsync(),
  provideNativeDateAdapter(),
  {provide: MAT_DATE_LOCALE, useValue: 'es-ES'},
  {provide: LOCALE_ID, useValue: 'es-ES'},

  provideStore(),
  provideState('core', coreReducer),
  provideState('home', homeReducer),
  provideState('schedule', scheduleReducer),
  provideState('outlet', combineReducers({outletDetailsReducer, ordersReducer, activityReducer})),
  provideEffects(CoreEffects, HomeEffects, ScheduleEffects, OutletDetailsEffects, OrdersEffects, ActivitiesEffects),

  !environment.production ? provideStoreDevtools({maxAge: 25, logOnly: environment.production}) : [],

  {provide: ENVIRONMENT, useValue: environment},

  importProvidersFrom(
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: (sessionService: AppSessionService) => ({
          tokenGetter: () => sessionService.currentToken?.() ?? null,
          allowedDomains: environment.allowedDomains,
        }),
        deps: [AppSessionService],
      },
    }),
    TranslateModule.forRoot({
      loader: {provide: TranslateLoader, useClass: WebpackTranslateLoader, deps: [HttpClient]},
    })
  ),
  provideTranslationsOnlyUveI18n({environment}, StreetTranslocoService),

  provideFirebaseApp(() => {
    const firebaseConfig = sandboxActive ? environment.sandbox.firebase_environemnt : environment.firebase_environemnt
    return initializeApp(firebaseConfig)
  }),
  provideFirestore(() => initializeFirestore(getApp(), {ignoreUndefinedProperties: true})),

  // Slave mode: Street manages its own auth (JWT), no OIDC. Disables auto-logout on 401.
  ...provideSlaveSessionService({authBaseUrl: uveApiClientEnv.authBaseUrl}, SHARED_CORE_EVENT_BUS),
  {provide: SessionService, useExisting: AppSessionService},

  provideApiClient(uveApiClientEnv),

  ...SERVICE_PROVIDERS,
  {
    provide: MONITORING_SINK,
    useFactory: (http: HttpMonitoringSink, queue: QueueMonitoringSink) =>
      localStorage.getItem(MONITORING_SINK_URL_KEY) ? http : queue,
    deps: [HttpMonitoringSink, QueueMonitoringSink],
  },

  // AI Core (Arquitectura Hexagonal para Agentes)
  provideAiCore(),
  // Initial Sync Core (Arquitectura Hexagonal local del sync inicial)
  provideInitialSyncCore(),
  provideMapCore(),

  // AI Chat framework
  provideAiChat({mastraBaseUrl: environment.amoUrl, storagePrefix: 'street:threads'}),
  provideAiChatAuth(() => createSessionAuthAdapter(inject(SessionService))),
  provideAiChatBuiltinComponents(),
  provideAiChatChartComponents(),
  provideAiChatAdvancedChartComponents(),

  provideToastNotification({closeButton: true, enableHtml: true}),
  provideAppInitializer(() => {
    const initializerFn = appInitializerFactory(inject(InitService))
    return initializerFn()
  }),

  ...eventBusProviders,

  ...provideSharedDomainPipes,
  UveTranslateStringPipe,
  SafeHtmlPipe,
  DayLabelPipe,
  TranslateStringV2Pipe,
  CurrencyPipe,
  DecimalPipe,
  UveNumberPipe,
  UveOrderNumberPipe,
  IsSyncPipe,
  UveTranslatePipe,
  SafePipe,
  TruncatePipe,
  FormatDatePipe,
  NoImagePipe,
  SecureNoImagePipe,
  ProductImagePipe,
  ProductConservationConditionTextPipe,

  {provide: TranslateLoader, useClass: WebpackTranslateLoader},
  {provide: MAT_SELECT_CONFIG, useValue: {overlayPanelClass: 'street-select-overlay'}},
  {provide: ChatContract, useClass: ChatFacade},
]
export const appConfig: ApplicationConfig = {providers}
